import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaUser, FaSearch, FaFilter, FaSort, FaEye, FaTrash, FaPlus, FaChevronDown, FaChevronUp, FaSortAmountDown, FaSortAmountUp, FaTable, FaTh, FaCalendarAlt, FaIdCard, FaSchool, FaPhone, FaGenderless, FaMale, FaFemale, FaTimes, FaFileExcel } from "react-icons/fa";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";

const StudentList = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClass, setSelectedClass] = useState("Hepsi");
  const [sortOrder, setSortOrder] = useState("Eskiden Yeniye");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("Hepsi");
  const [showFilters, setShowFilters] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [viewMode, setViewMode] = useState("cards"); // "cards" veya "table" olabilir
  const [classOptions, setClassOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("Hepsi");
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole") || "user";

  const uniquePeriods = Array.from(new Set(students.map(s => s.period).filter(Boolean)));

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    // Aktif filtre sayısını hesapla
    let count = 0;
    if (selectedClass !== "Hepsi") count++;
    if (selectedPeriod !== "Hepsi") count++;
    if (searchTerm) count++;
    if (selectedBranch !== "Hepsi") count++;
    setFilterCount(count);
  }, [selectedClass, selectedPeriod, searchTerm, selectedBranch]);

  const fetchStudents = async () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      setError("Kullanıcı girişi yapılmamış.");
      setLoading(false);
      toast.error("Oturum açmanız gerekiyor");
      return;
    }

    try {
      const timestamp = new Date().getTime();
      const response = await axios.get(`http://esnakademi.com/api/students?_t=${timestamp}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setStudents(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Veri çekme hatası:", error);
      setError("Veri çekme hatası: " + error.message);
      setLoading(false);
      toast.error("Öğrenci verileri yüklenemedi");
    }
  };

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const timestamp2 = new Date().getTime();
        const res = await axios.get(`http://esnakademi.com/api/classes?_t=${timestamp2}`, { headers:{Authorization:`Bearer ${token}`} });
        setClassOptions(res.data);
      } catch(e) { 
        console.error(e);
        toast.error("Sınıf verileri yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, []);

  useEffect(() => {
    if(selectedClass === "Hepsi") { setBranchOptions([]); setSelectedBranch("Hepsi"); return; }
    const cls = classOptions.find(c=>c.class_level.toString()===selectedClass.toString());
    if(!cls){ setBranchOptions([]); setSelectedBranch("Hepsi"); return; }
    (async ()=>{
      try {
        const token = localStorage.getItem("authToken");
        const timestamp = new Date().getTime();
        const res = await axios.get(`http://esnakademi.com/api/branches?class_id=${cls.id}&_t=${timestamp}`, { 
          headers:{
            Authorization:`Bearer ${token}`
          } 
        });
        setBranchOptions(res.data || []);
      } catch(e){ 
        setBranchOptions([]);
      } 
    })();
  }, [selectedClass, classOptions]);

  const filteredStudents = students
    .filter(
      (student) =>
        (selectedClass === "Hepsi" || student.student_class?.toString() === selectedClass?.toString()) &&
        (selectedPeriod === "Hepsi" || student.period === selectedPeriod) &&
        (selectedBranch === "Hepsi" || student.branch === selectedBranch) &&
        (searchTerm === "" || 
          student.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
          student.surname?.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortOrder === "Eskiden Yeniye") {
        return new Date(a.registration_date || "1970-01-01") - new Date(b.registration_date || "1970-01-01");
      } else {
        return new Date(b.registration_date || "1970-01-01") - new Date(a.registration_date || "1970-01-01");
      }
    });

  const parsePaymentPlan = (paymentPlanStr) => {
    if (!paymentPlanStr) return { totalAmount: 0, unpaidBills: [], paidInstallments: [] };
    
    try {
      let paymentPlan = paymentPlanStr;
      
      if (typeof paymentPlanStr === 'string') {
        // Tırnak işaretlerini düzeltme
        let cleanedString = paymentPlanStr
          .replace(/\\"/g, '"')
          .replace(/"{/g, '{')
          .replace(/}"/g, '}');
          
        if (cleanedString.startsWith('"') && cleanedString.endsWith('"')) {
          cleanedString = cleanedString.slice(1, -1);
        }
        
        try {
          paymentPlan = JSON.parse(cleanedString);
        } catch (innerError) {
          return { totalAmount: 0, unpaidBills: [], paidInstallments: [] };
        }
      }
      
      return {
        totalAmount: paymentPlan.totalAmount || 0,
        unpaidBills: Array.isArray(paymentPlan.unpaidBills) ? paymentPlan.unpaidBills : [],
        paidInstallments: Array.isArray(paymentPlan.paidInstallments) ? paymentPlan.paidInstallments : []
      };
    } catch (error) {
      return { totalAmount: 0, unpaidBills: [], paidInstallments: [] };
    }
  };

  const calculateRemainingPayment = (paymentPlan) => {
    const parsedPlan = parsePaymentPlan(paymentPlan);
    const totalAmount = parsedPlan.totalAmount || 0;
    const paid = (parsedPlan.paidInstallments || []).reduce(
      (total, bill) => total + parseFloat(bill.amount || 0),
      0
    );
    return totalAmount - paid;
  };

  const calculatePaymentPercentage = (paymentPlan) => {
    const parsedPlan = parsePaymentPlan(paymentPlan);
    const totalAmount = parsedPlan.totalAmount || 0;
    if (totalAmount === 0) return 0;
    
    const paid = (parsedPlan.paidInstallments || []).reduce(
      (total, bill) => total + parseFloat(bill.amount || 0),
      0
    );
    
    return Math.min(100, Math.max(0, (paid / totalAmount) * 100));
  };

  const handleDelete = async (id, studentName) => {
    const token = localStorage.getItem("authToken");
    
    if (window.confirm(`${studentName} isimli öğrenciyi silmek istediğinizden emin misiniz?`)) {
      // Finansal kayıtları da silinsin mi diye sor
      const deleteFinancialRecords = window.confirm(
        `${studentName} isimli öğrencinin alınmış ödemelerini de finansal sayfasından silmek istiyor musunuz?\n\n` +
        `• EVET: Öğrenci ve tüm ödemeleri silinir\n` +
        `• HAYIR: Sadece öğrenci silinir, ödemeler finansal sayfada kalır`
      );

      try {
        setLoading(true);
        
        // Eğer finansal kayıtlar da silinecekse, önce onları sil
        if (deleteFinancialRecords) {
          try {
            // Öğrencinin finansal kayıtlarını bul ve sil
            const financialResponse = await axios.get(
              `http://esnakademi.com/api/financial-records?_t=${Date.now()}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            const studentFinancialRecords = financialResponse.data.filter(record => 
              record.description && record.description.includes(studentName)
            );

            if (studentFinancialRecords.length > 0) {
              const deleteToast = toast.loading(`${studentFinancialRecords.length} adet ödeme kaydı siliniyor...`);
              
              // Her finansal kaydı tek tek sil
              for (const record of studentFinancialRecords) {
                try {
                  await axios.delete(
                    `http://esnakademi.com/api/financial-records?id=${record.id}`,
                    {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    }
                  );
                } catch (recordError) {
                  console.warn(`Finansal kayıt ${record.id} silinirken hata (devam edildi):`, recordError);
                }
              }

              toast.dismiss(deleteToast);
              toast.success(`${studentFinancialRecords.length} adet ödeme kaydı silindi`);
            }
          } catch (financialError) {
            console.error("Finansal kayıtlar silinirken hata:", financialError);
            toast.error("Bazı finansal kayıtlar silinemedi, ancak öğrenci silme işlemi devam ediyor");
          }
        }

        // Öğrenciyi sil
        await axios.delete(`http://esnakademi.com/api/students/delete?id=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        // Silme başarılı olduğunda öğrenci listesini güncelle
        setStudents(students.filter(student => student.id !== id));
        toast.success("Öğrenci başarıyla silindi");
      } catch (error) {
        console.error("Silme hatası:", error);
        toast.error("Öğrenci silinemedi");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditClick = (id) => {
    const student = students.find((s) => s.id === id);
    if (student) {
      navigate(`/student-list/student-details/${id}`, { state: { student } });
    } else {
      toast.error("Öğrenci bilgisi bulunamadı");
    }
  };

  const clearFilters = () => {
    setSelectedClass("Hepsi");
    setSelectedPeriod("Hepsi");
    setSearchTerm("");
    setSelectedBranch("Hepsi");
  };

  const exportToExcel = () => {
    try {
      // Excel başlıkları
      const headers = [
        "Öğrenci No",
        "Ad",
        "Soyad",
        "Sınıf",
        "Şube",
        "Dönem",
        "Telefon",
        "E-posta",
        "Cinsiyet",
        "Doğum Tarihi",
        "TC Kimlik No",
        "Adres",
        "Danışman Öğretmen",
        "Anne Adı",
        "Anne Telefon",
        "Baba Adı",
        "Baba Telefon",
        "Kayıt Tarihi",
        "Toplam Ücret",
        "Ödenen Tutar",
        "Kalan Tutar",
        "Ödeme Yüzdesi"
      ];

      // Filtrelenmiş öğrenci verilerini Excel formatına dönüştür
      const excelData = filteredStudents.map(student => {
        const paymentPlan = parsePaymentPlan(student.payment_plan);
        const totalAmount = paymentPlan.totalAmount || 0;
        const paidAmount = (paymentPlan.paidInstallments || []).reduce(
          (total, bill) => total + parseFloat(bill.amount || 0),
          0
        );
        const remainingAmount = totalAmount - paidAmount;
        const paymentPercentage = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;

        return [
          student.student_number || "",
          student.name || "",
          student.surname || "",
          student.student_class || "",
          student.branch || "",
          student.period || "",
          student.phone || "",
          student.email || "",
          student.gender === "male" ? "Erkek" : student.gender === "female" ? "Kız" : "",
          student.birth_date || "",
          student.tc_number || "",
          student.address || "",
          student.advisor_teacher || "",
          student.mother_name || "",
          student.mother_phone || "",
          student.father_name || "",
          student.father_phone || "",
          student.registration_date || "",
          totalAmount,
          paidAmount,
          remainingAmount,
          `${paymentPercentage}%`
        ];
      });

      // Excel dosyası oluştur
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Öğrenci Listesi");

      // Dosya adını oluştur
      const now = new Date();
      const dateStr = now.toLocaleDateString("tr-TR").replace(/\./g, "-");
      const timeStr = now.toLocaleTimeString("tr-TR", { hour: '2-digit', minute: '2-digit' }).replace(/:/g, "-");
      
      let fileName = `ogrenci_listesi_${dateStr}_${timeStr}`;
      
      // Aktif filtreleri dosya adına ekle
      const activeFilters = [];
      if (selectedClass !== "Hepsi") activeFilters.push(`${selectedClass}_sinif`);
      if (selectedBranch !== "Hepsi") activeFilters.push(`${selectedBranch}_sube`);
      if (selectedPeriod !== "Hepsi") activeFilters.push(`${selectedPeriod}_donem`);
      if (searchTerm) activeFilters.push(`arama_${searchTerm}`);
      
      if (activeFilters.length > 0) {
        fileName += `_${activeFilters.join("_")}`;
      }
      
      fileName += ".xlsx";

      // Dosyayı indir
      XLSX.writeFile(workbook, fileName);
      
      toast.success(`${filteredStudents.length} öğrenci Excel dosyasına aktarıldı`);
    } catch (error) {
      console.error("Excel export hatası:", error);
      toast.error("Excel dosyası oluşturulurken bir hata oluştu");
    }
  };

  const exportClassListToExcel = () => {
    try {
      // Sınıf listesi için başlıklar: Ad, Soyad, Numara, Sınıf
      const headers = [
        "Ad",
        "Soyad",
        "Öğrenci No",
        "Sınıf",
        "Şube"
      ];

      // Filtrelenmiş öğrenci verilerini sınıf listesi formatına dönüştür
      const excelData = filteredStudents.map(student => [
        student.name || "",
        student.surname || "",
        student.student_number || "",
        student.student_class ? `${student.student_class}. Sınıf` : "",
        student.branch || ""
      ]);

      // Excel dosyası oluştur
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sınıf Listesi");

      // Sütun genişliklerini ayarla
      worksheet['!cols'] = [
        { wch: 20 }, // Ad
        { wch: 20 }, // Soyad
        { wch: 15 }, // Öğrenci No
        { wch: 12 }, // Sınıf
        { wch: 12 }  // Şube
      ];

      // Dosya adını oluştur
      const now = new Date();
      const dateStr = now.toLocaleDateString("tr-TR").replace(/\./g, "-");
      const timeStr = now.toLocaleTimeString("tr-TR", { hour: '2-digit', minute: '2-digit' }).replace(/:/g, "-");
      
      let fileName = `sinif_listesi_${dateStr}_${timeStr}`;
      
      // Aktif filtreleri dosya adına ekle
      const activeFilters = [];
      if (selectedClass !== "Hepsi") activeFilters.push(`${selectedClass}_sinif`);
      if (selectedBranch !== "Hepsi") activeFilters.push(`${selectedBranch}_sube`);
      if (selectedPeriod !== "Hepsi") activeFilters.push(`${selectedPeriod}_donem`);
      
      if (activeFilters.length > 0) {
        fileName += `_${activeFilters.join("_")}`;
      }
      
      fileName += ".xlsx";

      // Dosyayı indir
      XLSX.writeFile(workbook, fileName);
      
      toast.success(`${filteredStudents.length} öğrenci sınıf listesi Excel dosyasına aktarıldı`);
    } catch (error) {
      console.error("Sınıf listesi Excel export hatası:", error);
      toast.error("Sınıf listesi Excel dosyası oluşturulurken bir hata oluştu");
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "table" ? "cards" : "table");
  };

  const getGenderIcon = (gender) => {
    if (gender === "male") return <FaMale className="text-blue-500" />;
    if (gender === "female") return <FaFemale className="text-pink-500" />;
    return <FaGenderless className="text-gray-500" />;
  };

  // Yükleme animasyonu
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Öğrenci verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Hata mesajı
  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-red-500 text-center text-5xl mb-4">
            <FaUser className="mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">Veri Yükleme Hatası</h2>
          <p className="text-gray-600 mb-6 text-center">{error}</p>
          <button
            onClick={() => fetchStudents()}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-6 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Üst Başlık ve İşlem Butonları */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
              <FaUser className="mr-3 text-blue-600" /> Öğrenci Listesi
            </h1>
            
            <div className="flex flex-wrap gap-2">
              <button
                onClick={toggleViewMode}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center transition-colors"
              >
                {viewMode === "cards" ? (
                  <>
                    <FaTable className="mr-2" /> Tablo Görünümü
                  </>
                ) : (
                  <>
                    <FaTh className="mr-2" /> Kart Görünümü
                  </>
                )}
              </button>
              
              <button
                onClick={() => navigate("/student-list/student-reg")}
                className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-blue-900 transition-all shadow-md flex items-center"
              >
                <FaPlus className="mr-2" /> Yeni Öğrenci Ekle
              </button>
            </div>
          </div>
        </div>

        {/* Arama ve Filtreleme */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
          <div className="p-5">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="relative flex-grow max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Öğrenci adı veya soyadı ara..."
                  className="pl-10 block w-full rounded-lg border-gray-300 bg-gray-50 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setSearchTerm("")}
                  >
                    <span className="text-gray-400 hover:text-gray-500">✕</span>
                  </button>
                )}
              </div>

              <button
                className={`flex items-center px-4 py-2 rounded-lg border ${
                  showFilters ? "border-blue-500 bg-blue-50 text-blue-600" : "border-gray-300 bg-white text-gray-700"
                }`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className="mr-2" />
                Filtreler
                {filterCount > 0 && (
                  <span className="ml-2 bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    {filterCount}
                  </span>
                )}
                {showFilters ? <FaChevronUp className="ml-2" /> : <FaChevronDown className="ml-2" />}
              </button>

              <button
                className="flex items-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setSortOrder(sortOrder === "Eskiden Yeniye" ? "Yeniden Eskiye" : "Eskiden Yeniye")}
              >
                {sortOrder === "Eskiden Yeniye" ? (
                  <>
                    <FaSortAmountDown className="mr-2" /> Eskiden Yeniye
                  </>
                ) : (
                  <>
                    <FaSortAmountUp className="mr-2" /> Yeniden Eskiye
                  </>
                )}
              </button>

              <button
                className="flex items-center px-4 py-2 rounded-lg border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                onClick={exportToExcel}
                disabled={filteredStudents.length === 0}
              >
                <FaFileExcel className="mr-2" />
                Excel İndir ({filteredStudents.length})
              </button>

              <button
                className="flex items-center px-4 py-2 rounded-lg border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                onClick={exportClassListToExcel}
                disabled={filteredStudents.length === 0}
              >
                <FaFileExcel className="mr-2" />
                Sınıf Listesi İndir ({filteredStudents.length})
              </button>
            </div>

            {/* Genişletilebilir Filtre Bölümü */}
            {showFilters && (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4 transition-all duration-300 ease-in-out">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sınıf</label>
                    <select
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={selectedClass}
                      onChange={(e) => setSelectedClass(e.target.value)}
                    >
                      <option value="Hepsi">Tüm Sınıflar</option>
                      {classOptions.map((cls) => (
                        <option key={cls.id} value={cls.class_level}>{cls.class_level}. Sınıf</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dönem</label>
                    <select
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriod(e.target.value)}
                    >
                      <option value="Hepsi">Tüm Dönemler</option>
                      {uniquePeriods.map((p)=>(
                         <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Şube</label>
                    <select
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                    >
                      <option value="Hepsi">Tüm Şubeler</option>
                      {branchOptions.map((b) => (
                        <option key={b.id} value={b.branch_name}>{b.branch_name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex items-end">
                    <button
                      onClick={clearFilters}
                      className="w-full py-2 px-4 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Filtreleri Temizle
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Öğrenci Sayısı */}
            <div className="flex justify-between items-center text-sm text-gray-600">
              <div>
                <span className="font-medium">{filteredStudents.length}</span> öğrenci bulundu
                {filterCount > 0 && " (filtrelendi)"}
              </div>
            </div>
          </div>
        </div>

        {/* Öğrenci Listesi - Tablo Görünümü */}
        {viewMode === "table" && filteredStudents.length > 0 && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Öğrenci</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numara</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sınıf/Şube</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kayıt Tarihi</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ödeme Durumu</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                            {student.photoUrl ? (
                              <img
                                src={student.photoUrl}
                                alt={`${student.name} Fotoğrafı`}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex items-center justify-center">
                                {getGenderIcon(student.gender)}
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{student.name} {student.surname}</div>
                            <div className="text-sm text-gray-500">{student.email || "E-posta belirtilmemiş"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.student_number || "???"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.student_class || "?"}.Sınıf</div>
                        <div className="text-sm text-gray-500">{student.period || "Dönem belirtilmemiş"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {student.registration_date ? new Date(student.registration_date).toLocaleDateString('tr-TR') : "Belirtilmemiş"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="mr-2 flex-grow max-w-[100px]">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  calculateRemainingPayment(student.payment_plan) > 0
                                    ? "bg-yellow-500"
                                    : "bg-green-500"
                                }`}
                                style={{ width: `${calculatePaymentPercentage(student.payment_plan)}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="text-sm text-gray-900">
                            {calculateRemainingPayment(student.payment_plan) > 0
                              ? `${calculateRemainingPayment(student.payment_plan)} ₺ kaldı`
                              : "Tamamlandı"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEditClick(student.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Detaylar"
                        >
                          <FaEye />
                        </button>
                        {userRole === "admin" && (
                          <button
                            onClick={() => handleDelete(student.id, `${student.name} ${student.surname}`)}
                            className="text-red-600 hover:text-red-900"
                            title="Sil"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Öğrenci Listesi - Kart Görünümü */}
        {viewMode === "cards" && filteredStudents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudents.map((student) => (
              <div
                key={student.id}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-4 py-3 text-white">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <FaIdCard className="mr-2" />
                      <h3 className="font-semibold truncate">{student.student_number || "???"}</h3>
                    </div>
                    <div className="flex items-center">
                      <FaSchool className="mr-1 text-xs" />
                      <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
                        {student.student_class || "?"}.Sınıf {student.period || ""}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="p-5">
                  <div className="flex items-start mb-4">
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {student.photoUrl ? (
                        <img
                          src={student.photoUrl}
                          alt={`${student.name} Fotoğrafı`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-4xl">
                          {getGenderIcon(student.gender)}
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <h2 className="text-xl font-semibold text-gray-800">
                        {student.name} {student.surname}
                      </h2>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <FaCalendarAlt className="mr-1 text-gray-400" />
                        <p>
                          {student.registration_date ? new Date(student.registration_date).toLocaleDateString('tr-TR') : "Kayıt tarihi belirtilmemiş"}
                        </p>
                      </div>
                      {student.phone && (
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <FaPhone className="mr-1 text-gray-400" />
                          <p>{student.phone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Ödeme Durumu</span>
                      <span className={`font-medium ${calculateRemainingPayment(student.payment_plan) > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                        {calculateRemainingPayment(student.payment_plan) > 0
                          ? `${calculateRemainingPayment(student.payment_plan)} ₺ kaldı`
                          : "Tamamlandı"}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          calculateRemainingPayment(student.payment_plan) > 0
                            ? "bg-yellow-500"
                            : "bg-green-500"
                        }`}
                        style={{ width: `${calculatePaymentPercentage(student.payment_plan)}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-right">
                      %{calculatePaymentPercentage(student.payment_plan).toFixed(0)} tamamlandı
                    </div>
                  </div>
                  
                  <div className="flex justify-between gap-2">
                    <button
                      onClick={() => handleEditClick(student.id)}
                      className="flex-1 py-2 px-3 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      <FaEye className="mr-1" /> Detaylar
                    </button>
                    {userRole === "admin" && (
                      <button
                        onClick={() => handleDelete(student.id, `${student.name} ${student.surname}`)}
                        className="py-2 px-3 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !filteredStudents.length && (
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="text-gray-400 text-5xl mb-4">
                <FaUser className="mx-auto" />
              </div>
              <h2 className="text-2xl font-bold text-gray-700 mb-2">Öğrenci Bulunamadı</h2>
              <p className="text-gray-500 mb-6">Arama kriterlerinize uygun öğrenci kaydı bulunmamaktadır.</p>
              {filterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Filtreleri Temizle
                </button>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default StudentList;
